import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, parseApiError, type UserLookupResponse } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { displayClanName } from '../lib/displayName';

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// SeedingTablePanel — never render a raw Discord snowflake.
function shortId(discordId: string): string { return `…${discordId.slice(-6)}`; }
import {
  Search,
  Users,
  Building2,
  Trophy,
  Clock,
  Sparkles,
  Trash2,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';

function ConsentedUsersPanel({ onPickUser }: { onPickUser: (discordId: string) => void }) {
  const [showOrphansOnly, setShowOrphansOnly] = useState(false);
  const usersQuery = useQuery({
    queryKey: ['admin', 'consented-users'],
    queryFn: () => api.admin.consentedUsers(),
  });

  const users = usersQuery.data?.users ?? [];
  const filtered = showOrphansOnly ? users.filter(u => u.membershipCount === 0) : users;

  return (
    <Card className="mb-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
          <Users className="h-3.5 w-3.5" /> Consented users
          {usersQuery.data && (
            <span className="rounded bg-surface-700 px-1.5 py-0.5 text-[10px]">
              {filtered.length}{showOrphansOnly && users.length !== filtered.length ? ` of ${users.length}` : ''}
            </span>
          )}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
          <input
            type="checkbox"
            checked={showOrphansOnly}
            onChange={e => setShowOrphansOnly(e.target.checked)}
            className="h-3 w-3"
          />
          Orphans only
        </label>
      </div>

      {usersQuery.isLoading && <p className="text-xs text-gray-500">Loading…</p>}
      {usersQuery.error && <p className="text-xs text-red-400">{(usersQuery.error as Error).message}</p>}
      {usersQuery.data && filtered.length === 0 && (
        <p className="text-xs text-gray-500">
          {showOrphansOnly ? 'No orphan users — all consented users have memberships.' : 'No consented users yet.'}
        </p>
      )}

      {filtered.length > 0 && (
        <ul className="max-h-72 space-y-1 overflow-y-auto pr-1">
          {filtered.map(u => (
            <li key={u.discordId}>
              <button
                onClick={() => onPickUser(u.discordId)}
                className={cn('flex w-full items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-left transition-colors hover:bg-white/[0.06]', MORPH)}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  {u.avatar ? (
                    <img
                      src={`https://cdn.discordapp.com/avatars/${u.discordId}/${u.avatar}.png?size=64`}
                      alt=""
                      className="h-8 w-8 shrink-0 rounded-full"
                    />
                  ) : (
                    <div className="h-8 w-8 shrink-0 rounded-full bg-surface-700" />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium text-white truncate">
                      {u.globalName || u.displayName || u.discordUsername || shortId(u.discordId)}
                      {u.discordClanTag && (
                        <span className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-ink-secondary">
                          {u.discordClanTag}
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-gray-500 truncate">
                      {u.discordId}{u.gamertag ? ` · ${u.gamertag}` : ''}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="rounded bg-white/[0.05] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ink-secondary">
                    {u.membershipCount === 0 ? 'Orphan' : `${u.membershipCount} mship${u.membershipCount === 1 ? '' : 's'}`}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function CompBrowser({ onPickMember }: { onPickMember: (discordId: string) => void }) {
  const [selectedComp, setSelectedComp] = useState<{ id: string; name: string } | null>(null);
  const [selectedClan, setSelectedClan] = useState<string | null>(null);

  const compsQuery = useQuery({
    queryKey: ['admin', 'browse-comps'],
    queryFn: () => api.competitions(),
  });

  const clansQuery = useQuery({
    queryKey: ['admin', 'browse-clans', selectedComp?.id],
    queryFn: () => api.competitionClanList(selectedComp!.id),
    enabled: !!selectedComp,
  });

  const membersQuery = useQuery({
    queryKey: ['admin', 'browse-members', selectedClan],
    queryFn: () => api.clanMembers(selectedClan!),
    enabled: !!selectedClan,
  });

  return (
    <Card className="mb-4">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
        <Trophy className="h-3.5 w-3.5" /> Browse
        {selectedComp && (
          <>
            <ChevronRight className="h-3.5 w-3.5 text-gray-700" />
            <button
              onClick={() => { setSelectedClan(null); }}
              className="text-ink-secondary hover:text-ink-primary transition-colors"
            >
              {selectedComp.name}
            </button>
          </>
        )}
        {selectedClan && (
          <>
            <ChevronRight className="h-3.5 w-3.5 text-gray-700" />
            <span className="text-ink-primary">{displayClanName(selectedClan.replace(/^CLAN#/, ''))}</span>
          </>
        )}
      </div>

      {!selectedComp && (
        <>
          {compsQuery.isLoading && <p className="text-xs text-gray-500">Loading comps…</p>}
          {compsQuery.error && <p className="text-xs text-red-400">{(compsQuery.error as Error).message}</p>}
          <div className="space-y-1.5">
            {(compsQuery.data ?? []).map((comp) => (
              <button
                key={comp.id}
                onClick={() => setSelectedComp({ id: comp.id, name: comp.name })}
                className={cn('flex w-full items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-left transition-colors hover:bg-white/[0.06]', MORPH)}
              >
                <div>
                  <div className="text-sm font-medium text-white">{comp.name}</div>
                  <div className="text-[10px] text-gray-500">
                    {comp.status ?? '—'} · {comp.stage ?? '—'} · team size {comp.teamSize ?? '—'}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-600" />
              </button>
            ))}
            {compsQuery.data && compsQuery.data.length === 0 && (
              <p className="text-xs text-gray-500">No competitions found.</p>
            )}
          </div>
        </>
      )}

      {selectedComp && !selectedClan && (
        <>
          <button
            onClick={() => setSelectedComp(null)}
            className="mb-3 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-400 hover:text-gray-200"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> All competitions
          </button>
          {clansQuery.isLoading && <p className="text-xs text-gray-500">Loading clans…</p>}
          {clansQuery.error && <p className="text-xs text-red-400">{(clansQuery.error as Error).message}</p>}
          <div className="space-y-1.5">
            {(clansQuery.data ?? []).map((clan) => (
              <button
                key={clan.id}
                onClick={() => setSelectedClan(clan.id)}
                className={cn('flex w-full items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-left transition-colors hover:bg-white/[0.06]', MORPH)}
              >
                <div>
                  <div className="text-sm font-medium text-white">{clan.name}</div>
                  <div className="text-[10px] text-gray-500">{clan.status ?? '—'}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-600" />
              </button>
            ))}
            {clansQuery.data && clansQuery.data.length === 0 && (
              <p className="text-xs text-gray-500">No clans in this competition.</p>
            )}
          </div>
        </>
      )}

      {selectedClan && (
        <>
          <button
            onClick={() => setSelectedClan(null)}
            className="mb-3 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-400 hover:text-gray-200"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Back to clans
          </button>
          {membersQuery.isLoading && <p className="text-xs text-gray-500">Loading members…</p>}
          {membersQuery.error && <p className="text-xs text-red-400">{(membersQuery.error as Error).message}</p>}
          <div className="space-y-1.5">
            {(membersQuery.data ?? []).map((m) => (
              <button
                key={m.discordId}
                onClick={() => onPickMember(m.discordId)}
                className={cn('flex w-full items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-left transition-colors hover:bg-white/[0.06]', MORPH)}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">
                    {m.displayName || m.gamertag || shortId(m.discordId)}
                  </div>
                  <div className="font-mono text-[10px] text-gray-500 truncate">{m.discordId}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-semibold uppercase ${
                    String(m.scopedRole ?? '').includes('LEADER') ? 'text-amber-400' :
                    String(m.scopedRole ?? '').includes('OWNER') ? 'text-yellow-400' :
                    'text-gray-500'
                  }`}>
                    {m.scopedRole ?? m.role ?? ''}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                </div>
              </button>
            ))}
            {membersQuery.data && membersQuery.data.length === 0 && (
              <p className="text-xs text-gray-500">No members in this clan.</p>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

function PurgeUserPanel({ discordId, displayName }: { discordId: string; displayName: string }) {
  const qc = useQueryClient();
  const [confirmInput, setConfirmInput] = useState('');
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const purgeMut = useMutation({
    mutationFn: () => api.admin.purgeUser(discordId),
    onSuccess: (res) => {
      setResult(`Purged ${res.itemsProcessed} item${res.itemsProcessed === 1 ? '' : 's'}.`);
      qc.invalidateQueries({ queryKey: ['admin', 'user-lookup'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err) => setResult(`Failed: ${parseApiError(err).message}`),
  });

  const canSubmit = confirmInput.trim() === discordId && !purgeMut.isPending;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={cn('flex items-center gap-2 rounded-lg bg-red-900/25 px-3 py-2 text-xs font-bold uppercase tracking-wider text-red-300 transition-colors hover:bg-red-900/40', MORPH)}
      >
        <Trash2 className="h-3.5 w-3.5" /> POPIA Purge
      </button>
    );
  }

  return (
    <Card>
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-6 w-6 shrink-0 text-red-400" />
        <div className="flex-1 space-y-3">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-red-200">
              POPIA Purge — irreversible
            </h3>
            <p className="mt-1 text-xs text-iron-300">
              Deletes all USER#, membership, and activity rows for{' '}
              <span className="font-mono text-white">{displayName}</span>{' '}
              (<span className="font-mono">{discordId}</span>). They become a fresh public user.
            </p>
          </div>
          <div>
            <label className="label">
              Type the Discord ID to confirm
            </label>
            <input
              type="text"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={discordId}
              className="input mt-1 w-full font-mono"
            />
          </div>
          {result && (
            <p className={`text-xs ${result.startsWith('Purged') ? 'text-green-400' : 'text-red-400'}`}>
              {result}
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => purgeMut.mutate()}
              disabled={!canSubmit}
              className={cn('flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-red-500 disabled:opacity-40', MORPH)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {purgeMut.isPending ? 'Purging...' : 'Confirm Purge'}
            </button>
            <button
              onClick={() => { setOpen(false); setConfirmInput(''); setResult(null); }}
              className="btn-ghost text-xs font-bold uppercase tracking-wider"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ProfileCard({ profile, discordId }: { profile: UserLookupResponse['profile']; discordId: string }) {
  const qc = useQueryClient();
  const enrichMut = useMutation({
    mutationFn: () => api.admin.enrichUser(discordId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-lookup'] }),
  });

  const accountAge = profile?.accountCreatedAt
    ? Math.floor((Date.now() - profile.accountCreatedAt) / 86400000)
    : null;

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            {profile?.discordAvatar && (
              <img
                src={`https://cdn.discordapp.com/avatars/${discordId}/${profile.discordAvatar}.png?size=64`}
                alt=""
                className="h-10 w-10 rounded-full"
              />
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-white">
                  {profile?.discordUsername || profile?.gamertag || shortId(discordId)}
                </span>
                {profile?.isSpammer && (
                  <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-bold text-red-400">
                    SPAMMER
                  </span>
                )}
                {accountAge !== null && accountAge < 30 && (
                  <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold text-amber-400">
                    NEW ACCOUNT
                  </span>
                )}
              </div>
              <p className="font-mono text-xs text-gray-500">{discordId}</p>
            </div>
          </div>
          {profile?.gamertag && (
            <p className="text-sm text-gray-400">
              Gamertag: <span className="font-medium text-white">{profile.gamertag}</span>
            </p>
          )}
          {accountAge !== null && (
            <p className="text-xs text-gray-600">Account age: {accountAge} days</p>
          )}
        </div>
        <button
          onClick={() => enrichMut.mutate()}
          disabled={enrichMut.isPending}
          className={cn('flex items-center gap-1 rounded-lg bg-white/[0.04] px-2.5 py-1.5 text-xs text-ink-secondary transition-colors hover:bg-white/[0.07] hover:text-ink-primary disabled:opacity-40', MORPH)}
        >
          <Sparkles className="h-3 w-3" />
          {enrichMut.isPending ? 'Enriching...' : 'Enrich from Discord'}
        </button>
      </div>
    </Card>
  );
}

function MembershipSection({
  title,
  icon: Icon,
  items,
  idKey,
  linkPrefix,
}: {
  title: string;
  icon: typeof Users;
  items: Array<{ [key: string]: unknown }>;
  idKey: string;
  linkPrefix?: string;
}) {

  if (items.length === 0) {
    return (
      <Card>
        <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
          <Icon className="h-3.5 w-3.5" /> {title}
        </h3>
        <p className="text-xs text-gray-600">None</p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
        <Icon className="h-3.5 w-3.5" /> {title}
        <span className="rounded bg-surface-700 px-1.5 py-0.5 text-[10px]">{items.length}</span>
      </h3>
      <div className="space-y-1.5">
        {items.map((item) => {
          const id = String(item[idKey] ?? '');
          const role = String(item.role ?? '');
          const joinedAt = item.joinedAt as number | undefined;
          return (
            <div key={id} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {linkPrefix ? (
                    <Link to={`${linkPrefix}/${encodeURIComponent(id)}`} className="text-sm font-medium text-white hover:text-ink-secondary transition-colors truncate">
                      {id}
                    </Link>
                  ) : (
                    <span className="text-sm font-medium text-white truncate">{id}</span>
                  )}
                  <span className={`text-[10px] font-semibold uppercase ${
                    role.includes('OWNER') ? 'text-yellow-400' :
                    role.includes('ADMIN') ? 'text-ink-secondary' :
                    role.includes('LEADER') ? 'text-amber-400' :
                    'text-gray-500'
                  }`}>
                    {role}
                  </span>
                </div>
                {item.gamertag ? <p className="text-[10px] text-gray-600">{String(item.gamertag)}</p> : null}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {joinedAt && (
                  <span className="text-[10px] text-gray-600">{new Date(joinedAt).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  'user.player_submit': { label: 'Submitted screenshot', color: 'text-blue-400' },
  'admin.move_clan_member': { label: 'Moved between clans', color: 'text-violet-400' },
  'admin.remove_clan_member': { label: 'Removed from clan', color: 'text-red-400' },
  'admin.force_claim_seat': { label: 'Force-claimed seat', color: 'text-amber-400' },
  'admin.swap_seat': { label: 'Seat swapped', color: 'text-amber-400' },
  'admin.grant_org_credits': { label: 'Granted org credits', color: 'text-green-400' },
  'admin.grant_platform_credits': { label: 'Granted platform credits', color: 'text-green-400' },
  'admin.transfer_leadership': { label: 'Leadership transferred', color: 'text-yellow-400' },
  'admin.bulk_import': { label: 'Bulk import', color: 'text-violet-400' },
  'admin.activate_competition': { label: 'Activated competition', color: 'text-green-400' },
  'admin.end_competition': { label: 'Ended competition', color: 'text-red-400' },
  'admin.advance_stage': { label: 'Advanced stage', color: 'text-violet-400' },
  'comp.create': { label: 'Created competition', color: 'text-green-400' },
  'org.add_member': { label: 'Added to org', color: 'text-green-400' },
  'org.remove_member': { label: 'Removed from org', color: 'text-red-400' },
  'org.update_member_role': { label: 'Role updated', color: 'text-violet-400' },
};

function ActivityFeed({ activities }: { activities: UserLookupResponse['recentActivity'] }) {
  if (activities.length === 0) return null;

  return (
    <Card>
      <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
        <Clock className="h-3.5 w-3.5" /> Recent Activity
        <span className="rounded bg-surface-700 px-1.5 py-0.5 text-[10px]">{activities.length}</span>
      </h3>
      <div className="max-h-72 overflow-y-auto space-y-1">
        {activities.map((a) => {
          const meta = ACTION_LABELS[a.action] ?? { label: a.action, color: 'text-gray-400' };
          return (
            <div key={a.sk} className="flex items-center gap-2 rounded bg-white/[0.02] px-2.5 py-1.5">
              <span className={`text-xs font-medium ${meta.color} min-w-[10rem] shrink-0`}>{meta.label}</span>
              <span className="text-[10px] text-gray-600 truncate flex-1">{a.target}</span>
              <span className="text-[10px] text-gray-600 shrink-0">
                {new Date(a.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function UserLookup() {
  const [searchId, setSearchId] = useState('');
  const [activeId, setActiveId] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'user-lookup', activeId],
    queryFn: () => api.admin.userLookup(activeId),
    enabled: !!activeId,
  });

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const isFounder = me?.role === 'founder';

  const handleSearch = () => {
    const trimmed = searchId.trim();
    if (trimmed) setActiveId(trimmed);
  };

  return (
    <div>
      <div className={cn('mb-8 flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <Search className="h-7 w-7 text-accent-500" />
        <div>
          <h1 className="text-2xl font-bold text-white">User Lookup</h1>
          <p className="text-xs text-gray-500">Search by Discord ID to view full user profile, memberships, and activity</p>
        </div>
      </div>

      {/* Founder-only: list of all consented users (catches orphans) */}
      {isFounder && (
        <div className={SETTLE} style={settleDelay(1)}>
          <ConsentedUsersPanel onPickUser={(id) => { setSearchId(id); setActiveId(id); }} />
        </div>
      )}

      {/* Drill-down by competition → clan → member */}
      <div className={SETTLE} style={settleDelay(2)}>
        <CompBrowser onPickMember={(id) => { setSearchId(id); setActiveId(id); }} />
      </div>

      {/* Power-user: search by raw Discord ID */}
      <div className={SETTLE} style={settleDelay(3)}>
        <Card className="mb-6">
          <label className="label">
            Or search by Discord ID
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Discord User ID (18-digit snowflake)"
              value={searchId}
              onChange={e => setSearchId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="input flex-1 font-mono"
            />
            <button
              onClick={handleSearch}
              disabled={!searchId.trim()}
              className={cn('btn-primary flex items-center gap-2', MORPH)}
            >
              <Search className="h-4 w-4" /> Search
            </button>
          </div>
        </Card>
      </div>

      {isLoading && <PageSpinner />}
      {error && (
        <Card>
          <p className="text-sm text-red-400">{(error as Error).message}</p>
        </Card>
      )}

      {data && (
        <div className="space-y-4">
          <ProfileCard profile={data.profile} discordId={data.discordId} />

          {isFounder && (
            <PurgeUserPanel
              discordId={data.discordId}
              displayName={data.profile?.discordUsername || data.profile?.gamertag || shortId(data.discordId)}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MembershipSection
              title="Clans"
              icon={Users}
              items={data.clanMemberships}
              idKey="clanId"
              linkPrefix="/clan"
            />
            <MembershipSection
              title="Organizations"
              icon={Building2}
              items={data.orgMemberships}
              idKey="orgId"
            />
            <MembershipSection
              title="Competitions"
              icon={Trophy}
              items={data.compMemberships}
              idKey="compId"
            />
          </div>

          <ActivityFeed activities={data.recentActivity} />
        </div>
      )}
    </div>
  );
}
